import React from "react"
import CloseModal from "../CloseModal"
import { Button } from "../ui/button"
import { useNavigate } from "react-router-dom"

interface Props {
  startTime: Date
  coachPublicProfileData: {
    first_name: string
    last_name: string
    endpoint_slug: string
    [key: string]: any
  }
  durationInSeconds: number
}

const ConsultationSuccessPopup: React.FC<Props> = ({
  startTime,
  coachPublicProfileData,
  durationInSeconds,
}) => {
  const navigate = useNavigate()
  const BASE_URL = process.env.REACT_APP_BASE_URL

  const handleClose = () => {
    navigate(`/providers/${coachPublicProfileData.endpoint_slug}`)
  }

  const formatStartTimeCustom = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      timeZoneName: "short",
    }

    let formatted = new Intl.DateTimeFormat("en-US", options).format(date)
    formatted = formatted.replace(",", "")
    formatted = formatted.replace(" ", ", ")
    const parts = formatted.split(", ")

    if (parts.length === 3) {
      return `${parts[0]}, ${parts[1]} at ${parts[2]}`
    }

    return formatted
  }

  const handleDownloadICS = () => {
    const start = new Date(startTime)
    const end = new Date(start.getTime() + durationInSeconds * 1000)

    const formatICS = (date: Date) =>
      date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z"

    const icsContent = `
      BEGIN:VCALENDAR
      VERSION:2.0
      PRODID:-//Zoee//EN
      BEGIN:VEVENT
      UID:${Date.now()}@zoee.com
      DTSTAMP:${formatICS(new Date())}
      DTSTART:${formatICS(start)}
      DTEND:${formatICS(end)}
      SUMMARY:Consultation with Coach ${coachPublicProfileData.first_name}
      DESCRIPTION:Your scheduled session on Zoee
      LOCATION:${BASE_URL}/member/sessions
      END:VEVENT
      END:VCALENDAR
    `.trim()

    const blob = new Blob([icsContent], { type: "text/calendar" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `Consultation_with_${coachPublicProfileData.first_name}.ics`
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleAddToGoogleCalendar = () => {
    const startUTC =
      new Date(startTime).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z"
    const endUTC =
      new Date(new Date(startTime).getTime() + durationInSeconds * 1000)
        .toISOString()
        .replace(/[-:]/g, "")
        .split(".")[0] + "Z"

    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
      `Consultation with Coach ${coachPublicProfileData.first_name}`
    )}&dates=${startUTC}/${endUTC}&details=${encodeURIComponent(
      "Your scheduled session on Zoee"
    )}&location=${encodeURIComponent(`${BASE_URL}/member/sessions`)}`

    window.open(url, "_blank")
  }

  return (
    <div className="fixed w-screen h-screen top-0 left-0 zoee-backdrop-filter z-[9001] flex items-center justify-center">
      <div className="p-16 md:w-[45%] bg-white main-shadow rounded-[16px] flex flex-col relative overflow-y-auto">
        <CloseModal
          styling="absolute top-4 right-4 text-black hover:text-gray-600"
          callback={handleClose}
        />

        <h1 className="font-bold text-black text-4xl font-semibold mb-8 mt-4">
          Just like that, you are scheduled to meet with Coach{" "}
          {coachPublicProfileData.first_name} on:
        </h1>

        <div className="flex flex-col gap-4 text-lg text-left break-words">
          <p className="font-bold mb-4">{formatStartTimeCustom(startTime)}</p>
          <p>
            A confirmation email will be sent to you shortly with all the
            session details.
          </p>
          <p className="-mt-4">
            {"\u{1F449}"} Be sure to check your spam or junk folder - just in
            case!
          </p>
          <div className="flex flex-col gap-4 mt-8 sm:flex-row">
            <Button onClick={handleAddToGoogleCalendar} variant="rounded">
              Add to Google Calendar
            </Button>
            <Button
              onClick={handleDownloadICS}
              variant="outline"
              className="rounded-xl"
            >
              Download .ics
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConsultationSuccessPopup
